/**
 * Shared PDP selection inventory fetch (IM-004 / IM-005).
 * Single React Query key for warehouse-scoped getProducts by selection product IDs.
 */

import {useMemo} from 'react';

import {useProducts} from '@amblique/king-living/app/hooks/use-products';
import {usePdpShopperInventoryIds} from '@amblique/king-living/app/hooks/use-pdp-shopper-inventory-ids';
import {GET_PRODUCTS_PARAMS} from '@amblique/king-living/app/utils/api-utils';
import {collectPdpLineItemProductIds} from '@amblique/king-living/app/utils/cart-utils';

/**
 * Stable, sorted SCAPI product IDs for a PDP selection inventory batch.
 *
 * @param {Object|null|undefined} selections
 * @param {Object} [options]
 * @param {Object|null|undefined} [options.variant]
 * @param {Object|null|undefined} [options.product]
 * @returns {string[]}
 */
export function buildPdpInventoryProductIds(selections, {variant, product} = {}) {
  const ids = new Set(collectPdpLineItemProductIds(selections));
  const coverId = selections?.cover?.productId ?? selections?.cover?.variantId;
  const primaryProductId =
    coverId ?? variant?.productId ?? product?.id ?? product?.productId ?? null;

  if (primaryProductId) {
    ids.add(primaryProductId);
  }

  return [...ids].filter(Boolean).sort();
}

/**
 * @param {Object} options
 * @param {string[]} options.productIds - Sorted unique SCAPI product IDs
 * @param {boolean} [options.enabled=true]
 * @returns {{
 *   productIds: string[],
 *   products: Array,
 *   productsById: Record<string, Object>,
 *   isPending: boolean,
 * }}
 */
export function usePdpSelectionInventoryProducts({productIds, enabled = true}) {
  const {shopperInventoryIds, isInventoryIdsResolving} = usePdpShopperInventoryIds();
  const idsKey = productIds.length > 0 ? productIds.join(',') : '';
  const shouldFetch = enabled && productIds.length > 0 && !isInventoryIdsResolving;

  const {data, isLoading, isFetching} = useProducts(
    {
      parameters: {
        ...GET_PRODUCTS_PARAMS,
        ids: idsKey,
      },
    },
    {
      enabled: shouldFetch,
      staleTime: 0,
    },
    {inventoryIds: shopperInventoryIds}
  );

  const products = data?.data ?? [];

  const productsById = useMemo(() => {
    const map = {};

    products.forEach((item) => {
      if (item?.id) {
        map[item.id] = item;
      }
    });

    return map;
  }, [products]);

  const isPending =
    (enabled && isInventoryIdsResolving) ||
    (shouldFetch && (isLoading || isFetching));

  return {
    isPending,
    productIds,
    products,
    productsById,
  };
}

export default usePdpSelectionInventoryProducts;
